/**
 * HHMS frontend permission matrix.
 *
 * Mirrors backend/app/core/permissions.py. Keep the two in sync.
 * This file is the single source of truth for UI-side gating
 * (route guards, sidebar visibility, action buttons).
 *
 * Role codes must match the backend RoleType enum exactly.
 */

// ── Role constants ─────────────────────────────────────────────────────
export const ROLES = {
  ADMIN: 'ADMIN',
  DOCTOR: 'DOCTOR',
  NURSE: 'NURSE',
  PATIENT: 'PATIENT',
  ORGANIZATION: 'ORGANIZATION',
  PROVIDER: 'PROVIDER',
  CAREGIVER: 'CAREGIVER',
  BILLING: 'BILLING',
  QA: 'QA',
};

export const CLINICIANS = [ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE];
export const STAFF = [
  ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE,
  ROLES.PROVIDER, ROLES.CAREGIVER, ROLES.BILLING,
];
export const ALL_ROLES = Object.values(ROLES);

// ── Permission matrix (UI) ─────────────────────────────────────────────
// FALLBACK ONLY — the live RBAC manifest (backend/config/rbac.yaml) is the
// source of truth and takes precedence. This matrix must MIRROR the yaml's
// page.* grants exactly; a looser entry here would show nav/pages the
// server would then 403, and a MISSING key fails closed and hides pages
// the role legitimately holds (synced 2026-07-18, yaml revision 12).
export const PERMISSIONS = {
  // Navigation / pages — mirror of rbac.yaml
  'page.dashboard':      ALL_ROLES,
  'page.patients':       [...CLINICIANS, ROLES.BILLING],
  'page.nurses':         [ROLES.ADMIN],
  'page.doctors':        [ROLES.ADMIN],
  'page.organizations':  [ROLES.ADMIN],
  'page.referrals':      CLINICIANS,
  'page.requests':       [...CLINICIANS, ROLES.BILLING, ROLES.PATIENT],
  'page.dispatch':       [ROLES.ADMIN],
  'page.visits':         [...CLINICIANS, ROLES.PATIENT],
  'page.care_plans':     [...CLINICIANS, ROLES.PATIENT],
  'page.clinical':       CLINICIANS,
  'page.prescriptions':  [...CLINICIANS, ROLES.PATIENT],
  'page.rpm':            [...CLINICIANS, ROLES.PATIENT],
  'page.billing':        [ROLES.ADMIN, ROLES.BILLING, ROLES.PATIENT],
  'page.billing_admin':  [ROLES.ADMIN, ROLES.BILLING],
  'page.analytics':      [ROLES.ADMIN, ROLES.DOCTOR, ROLES.BILLING, ROLES.QA],
  'page.audit':          [ROLES.ADMIN, ROLES.QA],
  'page.rbac':           [ROLES.ADMIN],
  'page.episodes':       [ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE, ROLES.PATIENT, ROLES.QA, ROLES.BILLING],
  'page.notifications':  ALL_ROLES,
  'page.settings':       ALL_ROLES,
  'page.profile':        ALL_ROLES,

  // Identity Platform (ss* / parties module) pages — yaml rev 12
  'page.ss_parties':          [...CLINICIANS, ROLES.BILLING],
  'page.ss_users':            [ROLES.ADMIN],
  'page.ss_roles':            [ROLES.ADMIN],
  'page.ss_permissions':      [ROLES.ADMIN],
  'page.ss_companies':        [ROLES.ADMIN],
  'page.ss_org_units':        [ROLES.ADMIN],
  'page.ss_relationships':    CLINICIANS,
  'page.ss_audit':            [ROLES.ADMIN],
  'page.ss_security_catalog': [ROLES.ADMIN],
  'page.ss_navigation':       [ROLES.ADMIN],
  'page.ss_lookup_config':    [ROLES.ADMIN],
  'page.ss_nurses':           [ROLES.ADMIN],
  'page.ss_doctors':          [ROLES.ADMIN],

  // Marketing / website settings — yaml rev 12
  'marketing.config.read':  [ROLES.ADMIN],
  'marketing.config.write': [ROLES.ADMIN],

  // Actions (button-level gating)
  'action.party.create':       [ROLES.ADMIN, ROLES.DOCTOR],
  'action.party.delete':       [ROLES.ADMIN],
  'action.patient.create':     [ROLES.ADMIN, ROLES.DOCTOR],
  'action.patient.edit':       [ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE],
  'action.nurse.create':       [ROLES.ADMIN],
  'action.nurse.assign':       [ROLES.ADMIN],
  'action.doctor.create':      [ROLES.ADMIN],
  'action.org.create':         [ROLES.ADMIN],
  'action.referral.create':    [ROLES.ADMIN, ROLES.DOCTOR],
  'action.request.create':     [ROLES.ADMIN, ROLES.DOCTOR, ROLES.PATIENT],
  'action.request.approve':    [ROLES.ADMIN, ROLES.DOCTOR],
  'action.visit.create':       [ROLES.ADMIN, ROLES.DOCTOR],
  'action.visit.checkin':      [ROLES.ADMIN, ROLES.NURSE],
  'action.careplan.create':    [ROLES.ADMIN, ROLES.DOCTOR],
  'action.careplan.update':    [ROLES.ADMIN, ROLES.DOCTOR, ROLES.NURSE],
  'action.prescription.create':[ROLES.ADMIN, ROLES.DOCTOR],
  'action.rpm.submit':         [...CLINICIANS, ROLES.PATIENT],
  'action.billing.create':     [ROLES.ADMIN, ROLES.BILLING],
  'action.billing.pay':        [ROLES.ADMIN, ROLES.BILLING, ROLES.PATIENT],
  'action.audit.view':         [ROLES.ADMIN],
};

/**
 * can(userRoles, permission) → boolean
 * The core check function. Accepts the user's role list and a permission key.
 */
export function can(userRoles, permission) {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  const allowed = PERMISSIONS[permission];
  if (!allowed) {
    // Fail-closed: an unknown permission is denied.
    console.warn(`[permissions] Unknown permission key: ${permission}`);
    return false;
  }
  return allowed.some((r) => userRoles.includes(r));
}

/** Convenience: true if user has any of the given roles. */
export function hasAnyRole(userRoles, ...roles) {
  const flat = roles.flat();
  if (!userRoles) return false;
  return flat.some((r) => userRoles.includes(r));
}

/** Returns the list of permissions granted to a user. */
export function grantedPermissions(userRoles) {
  return Object.keys(PERMISSIONS).filter((p) => can(userRoles, p));
}
